import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import type { Dayjs } from "dayjs";

import {
  buildCommunicatedPatientIdSet,
  isPatientFinderCommunicationFilter,
  type PatientFinderCommunicationFilter,
  type PatientFinderGender,
} from "../aggregations";
import type { PatientFinderAppointmentMode } from "../helpers/patientFinderAppointmentMode";
import { buildCommunicatedRecipientIdSet } from "../helpers/buildCommunicatedRecipientIdSet";
import {
  DEFAULT_PATIENT_FINDER_AUDIENCE,
  getAvailablePatientFinderAudiences,
  onlyPatientRecipients,
  resolvePatientFinderAudiencePatients,
  type PatientFinderAudience,
} from "../helpers/patientFinderAudience";
import { buildPatientFinderResultSummary } from "../helpers/buildPatientFinderResultSummary";
import { formatPatientTaxIdentifierDisplay } from "../helpers/formatCrmCommunicationsDisplay";
import { formatAppointmentPeriodLabel } from "../helpers/resolveAppointmentFetchDateRange";
import { formatPatientListLabel } from "../patientLabels";
import {
  dayjsToIsoDate,
  getDefaultPatientFinderFormState,
  useCommunicationsPatientFinderSearch,
} from "../hooks/useCommunicationsPatientFinderSearch";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import type { CrmPatientSummary, FluxZapCommunicationRecord } from "../types";
import CommunicationsAppointmentPeriodFields from "./CommunicationsAppointmentPeriodFields";
import CommunicationsOptionalFilterSection from "./CommunicationsOptionalFilterSection";
import CommunicationsPatientFinderAudienceFilter from "./CommunicationsPatientFinderAudienceFilter";
import CommunicationsPatientFinderFilterCard from "./CommunicationsPatientFinderFilterCard";
import CommunicationsSegmentedFilterControl from "./CommunicationsSegmentedFilterControl";
import CommunicationsShortcutPatientListBlock from "./CommunicationsShortcutPatientListBlock";
import {
  communicationsRankFilledSearchFieldSx,
  communicationsRankTableToolbarSx,
} from "./communicationsSubsectionStyles";
import { patientFinderAgeFieldMinWidth } from "./patientFinderToggleStyles";

export interface CommunicationsFindPatientsDialogProps {
  open: boolean;
  onClose: () => void;
  allPatients: CrmPatientSummary[];
  communications: FluxZapCommunicationRecord[];
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
  /**
   * Públicos extras (opcionais). Sem eles, o filtro "Público" não aparece
   * e o dialog funciona exatamente como antes.
   */
  consentPatients?: CrmPatientSummary[];
  registeredClients?: CrmPatientSummary[];
  audiencesLoading?: boolean;
  /** Chamado ao abrir: liga a carga sob demanda dos públicos extras. */
  onRequestAudiences?: () => void;
  /** Ecommerce: sem o público "Pacientes atendidos" (nem dentro de "Todos"). */
  isEcommerceOrg?: boolean;
}

function parseOptionalInt(value: string): number | null {
  const t = value.trim();
  if (t === "") return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
}

const actionButtonSx = {
  height: 36,
  px: 2,
  borderRadius: 2,
  textTransform: "none" as const,
  whiteSpace: "nowrap" as const,
};

const patientFinderAgeFieldSx = {
  width: { xs: "100%", sm: "auto" },
  minWidth: { sm: patientFinderAgeFieldMinWidth },
  flex: { sm: `1 1 ${patientFinderAgeFieldMinWidth}px` },
};

function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

/** Busca client-side sobre o resultado já listado (paridade com BatchTaskDetailDialog). */
function matchesFindPatientsListSearch(
  patient: CrmPatientSummary,
  query: string,
): boolean {
  if (!query) return true;
  const label = formatPatientListLabel(patient).toLowerCase();
  const email = (patient.email ?? "").toLowerCase();
  const phone = (patient.phone ?? "").toLowerCase();
  const tax = (patient.taxIdentifierValue ?? "").toLowerCase();
  const taxDisplay = formatPatientTaxIdentifierDisplay(
    patient.taxIdentifierValue,
  ).toLowerCase();

  return (
    label.includes(query) ||
    email.includes(query) ||
    phone.includes(query) ||
    tax.includes(query) ||
    taxDisplay.includes(query)
  );
}

/**
 * Filtros independentes e opcionais; sem nenhum ativo, lista todos os pacientes.
 */
const CommunicationsFindPatientsDialog = memo(
  ({
    open,
    onClose,
    allPatients,
    communications,
    onOpenSendMessage,
    consentPatients,
    registeredClients,
    audiencesLoading = false,
    onRequestAudiences,
    isEcommerceOrg = false,
  }: CommunicationsFindPatientsDialogProps) => {
    const defaults = useMemo(() => getDefaultPatientFinderFormState(), []);

    const hasAudiences =
      consentPatients !== undefined || registeredClients !== undefined;
    const [audience, setAudience] = useState<PatientFinderAudience>(
      DEFAULT_PATIENT_FINDER_AUDIENCE,
    );
    // Person não tem consulta: com este público o filtro de consultas some.
    const appointmentFilterBlocked = audience === "registered";

    const availableAudiences = useMemo(
      () => getAvailablePatientFinderAudiences(isEcommerceOrg),
      [isEcommerceOrg],
    );

    const audienceSources = useMemo(
      () => ({
        appointmentPatients: allPatients,
        consentPatients: consentPatients ?? [],
        registeredClients: registeredClients ?? [],
      }),
      [allPatients, consentPatients, registeredClients],
    );

    // Contagem do botão de consentimento: a mesma lista filtrada do público,
    // não o tamanho cru. No ecommerce os atendidos não entram na subtração.
    const consentAudiencePatients = useMemo(
      () =>
        hasAudiences
          ? resolvePatientFinderAudiencePatients("consent", audienceSources, {
              includeAppointments: !isEcommerceOrg,
            })
          : [],
      [hasAudiences, audienceSources, isEcommerceOrg],
    );

    // "Todos" é calculado à parte: dá a contagem do botão e evita juntar duas vezes.
    const allAudiencePatients = useMemo(
      () =>
        hasAudiences
          ? resolvePatientFinderAudiencePatients("all", audienceSources, {
              includeAppointments: !isEcommerceOrg,
            })
          : [],
      [hasAudiences, audienceSources, isEcommerceOrg],
    );

    const audiencePatients = useMemo(() => {
      if (!hasAudiences) return allPatients;
      if (audience === "all") return allAudiencePatients;
      if (audience === "consent") return consentAudiencePatients;
      return resolvePatientFinderAudiencePatients(audience, audienceSources, {
        includeAppointments: !isEcommerceOrg,
      });
    }, [
      hasAudiences,
      allPatients,
      audience,
      allAudiencePatients,
      consentAudiencePatients,
      audienceSources,
      isEcommerceOrg,
    ]);

    // Público fora da lista disponível (ex.: org virou ecommerce): volta para "Todos".
    useEffect(() => {
      if (!availableAudiences.includes(audience)) {
        setAudience(DEFAULT_PATIENT_FINDER_AUDIENCE);
      }
    }, [availableAudiences, audience]);

    /**
     * Consentimento e clientes cadastrados ainda chegando: listar agora
     * traria um resultado incompleto. "Pacientes atendidos" já está pronto.
     */
    const waitingAudienceData =
      hasAudiences && audiencesLoading && audience !== "appointments";

    useEffect(() => {
      if (open) onRequestAudiences?.();
    }, [open, onRequestAudiences]);

    const [useAppointmentFilter, setUseAppointmentFilter] = useState(
      defaults.useAppointmentFilter,
    );
    const [appointmentMode, setAppointmentMode] =
      useState<PatientFinderAppointmentMode>(defaults.appointmentMode);
    const [gender, setGender] = useState<PatientFinderGender>(defaults.gender);
    const [communicationFilter, setCommunicationFilter] =
      useState<PatientFinderCommunicationFilter>(defaults.communicationFilter);
    const [minAge, setMinAge] = useState(defaults.minAge);
    const [maxAge, setMaxAge] = useState(defaults.maxAge);
    const [refDate, setRefDate] = useState(() => new Date());
    const [periodFrom, setPeriodFrom] = useState<Dayjs | null>(
      defaults.periodFrom,
    );
    const [periodTo, setPeriodTo] = useState<Dayjs | null>(defaults.periodTo);
    const [resultPatients, setResultPatients] = useState<
      CrmPatientSummary[] | null
    >(null);
    /** Congela se a listagem usou filtro de consultas (coluna última consulta). */
    const [resultShowLastAppointment, setResultShowLastAppointment] =
      useState(false);
    /** Refina o resultado já carregado (não altera searchPatients). */
    const [listSearchQuery, setListSearchQuery] = useState("");
    const [resultListGeneration, setResultListGeneration] = useState(0);

    const {
      isSearching,
      searchError,
      isLocationLoading,
      hasLocationActor,
      searchPatients,
      resetSearch,
    } = useCommunicationsPatientFinderSearch();

    // Com públicos extras, envios para Person/{id} também contam como comunicados.
    const communicatedIds = useMemo(
      () =>
        hasAudiences
          ? buildCommunicatedRecipientIdSet(communications)
          : buildCommunicatedPatientIdSet(communications),
      [communications, hasAudiences],
    );

    const appointmentPeriod = useMemo(
      () => ({
        from: dayjsToIsoDate(periodFrom),
        to: dayjsToIsoDate(periodTo),
      }),
      [periodFrom, periodTo],
    );

    const periodLabel = useMemo(
      () => formatAppointmentPeriodLabel(appointmentPeriod),
      [appointmentPeriod],
    );

    const hasAgeFilter = Boolean(minAge.trim() || maxAge.trim());

    const resultSummary = useMemo(
      () =>
        buildPatientFinderResultSummary({
          useAppointmentFilter,
          appointmentMode,
          appointmentPeriod,
          gender,
          communicationFilter,
          hasAgeFilter,
        }),
      [
        useAppointmentFilter,
        appointmentMode,
        appointmentPeriod,
        gender,
        communicationFilter,
        hasAgeFilter,
      ],
    );

    const needsLocationForSearch =
      useAppointmentFilter && (isLocationLoading || !hasLocationActor);

    const showAppointmentAlerts =
      useAppointmentFilter &&
      (isLocationLoading || (!isLocationLoading && !hasLocationActor));

    const showStatusAlerts = showAppointmentAlerts || searchError != null;

    const handleAppointmentModeChange = useCallback(
      (mode: PatientFinderAppointmentMode) => {
        setAppointmentMode(mode);
        setPeriodFrom(null);
        setPeriodTo(null);
      },
      [],
    );

    const applyDefaultFormState = useCallback(() => {
      const next = getDefaultPatientFinderFormState();
      setUseAppointmentFilter(next.useAppointmentFilter);
      setAppointmentMode(next.appointmentMode);
      setGender(next.gender);
      setCommunicationFilter(next.communicationFilter);
      setMinAge(next.minAge);
      setMaxAge(next.maxAge);
      setRefDate(new Date());
      setPeriodFrom(next.periodFrom);
      setPeriodTo(next.periodTo);
      setResultPatients(null);
      setResultShowLastAppointment(false);
      setListSearchQuery("");
      setResultListGeneration(0);
      setAudience(DEFAULT_PATIENT_FINDER_AUDIENCE);
      resetSearch();
    }, [resetSearch]);

    const handleAudienceChange = useCallback((next: PatientFinderAudience) => {
      setAudience(next);
      // Clientes cadastrados não têm consulta: desliga o filtro de consultas.
      if (next === "registered") setUseAppointmentFilter(false);
    }, []);

    useEffect(() => {
      if (!open) {
        resetSearch();
        return;
      }
      applyDefaultFormState();
    }, [open, applyDefaultFormState, resetSearch]);

    useEffect(() => {
      setResultPatients(null);
      setListSearchQuery("");
    }, [
      audience,
      useAppointmentFilter,
      appointmentMode,
      gender,
      communicationFilter,
      minAge,
      maxAge,
      periodFrom,
      periodTo,
    ]);

    const filteredResultPatients = useMemo(() => {
      if (resultPatients == null) return null;
      const q = normalizeSearch(listSearchQuery);
      if (!q) return resultPatients;
      return resultPatients.filter((p) => matchesFindPatientsListSearch(p, q));
    }, [resultPatients, listSearchQuery]);

    const hasActiveListSearch = listSearchQuery.trim() !== "";
    const resultTotalCount = resultPatients?.length ?? 0;
    const resultFilteredCount = filteredResultPatients?.length ?? 0;

    const handleList = useCallback(async () => {
      // Filtro de consultas: só Patients entram (Person não tem consulta).
      const basePatients = useAppointmentFilter
        ? onlyPatientRecipients(audiencePatients)
        : audiencePatients;
      const filtered = await searchPatients(
        basePatients,
        {
          useAppointmentFilter,
          appointmentQuery: useAppointmentFilter
            ? { mode: appointmentMode, period: appointmentPeriod }
            : undefined,
        },
        {
          gender,
          communicationFilter,
          communicatedPatientIds: communicatedIds,
          minAgeYears: parseOptionalInt(minAge),
          maxAgeYears: parseOptionalInt(maxAge),
          refDate,
        },
      );
      setResultShowLastAppointment(useAppointmentFilter);
      setResultPatients(filtered);
      setResultListGeneration((g) => g + 1);
    }, [
      audiencePatients,
      appointmentMode,
      appointmentPeriod,
      communicatedIds,
      communicationFilter,
      gender,
      maxAge,
      minAge,
      refDate,
      searchPatients,
      useAppointmentFilter,
    ]);

    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          <Stack spacing={0.25}>
            <Typography variant="subtitle1" fontWeight={700}>
              Encontrar pacientes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Combine filtros opcionais e liste pacientes da organização.
            </Typography>
          </Stack>
          <IconButton
            aria-label="Fechar"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              Ative apenas os filtros que precisar. Com todos desligados e sexo
              em &quot;Todos&quot;, a lista traz{" "}
              <strong>todos os pacientes</strong> da organização.
            </Typography>

            {hasAudiences ? (
              <CommunicationsPatientFinderAudienceFilter
                value={audience}
                onChange={handleAudienceChange}
                audiences={availableAudiences}
                appointmentCount={allPatients.length}
                consentCount={consentAudiencePatients.length}
                registeredCount={registeredClients?.length ?? 0}
                allCount={allAudiencePatients.length}
                loading={audiencesLoading}
                disabled={isSearching}
                isEcommerceOrg={isEcommerceOrg}
              />
            ) : null}

            {waitingAudienceData ? (
              <Alert
                severity="info"
                variant="outlined"
                sx={{ py: 0.5, alignItems: "center" }}
              >
                Carregando pacientes com consentimento e clientes cadastrados…
                A listagem fica disponível assim que terminar.
              </Alert>
            ) : null}

            {!appointmentFilterBlocked ? (
              <CommunicationsOptionalFilterSection
                title="Filtro de consultas"
                hintWhenOff="Ative para filtrar pacientes por consulta"
                enabled={useAppointmentFilter}
                onEnabledChange={setUseAppointmentFilter}
              >
                <CommunicationsAppointmentPeriodFields
                  appointmentMode={appointmentMode}
                  onAppointmentModeChange={handleAppointmentModeChange}
                  periodFrom={periodFrom}
                  periodTo={periodTo}
                  onPeriodFromChange={setPeriodFrom}
                  onPeriodToChange={setPeriodTo}
                  periodLabel={periodLabel}
                />
              </CommunicationsOptionalFilterSection>
            ) : null}

            {showStatusAlerts ? (
              <Stack spacing={1}>
                {useAppointmentFilter && isLocationLoading ? (
                  <Alert
                    severity="info"
                    variant="outlined"
                    sx={{ py: 0.5, alignItems: "center" }}
                  >
                    Carregando unidades da clínica…
                  </Alert>
                ) : null}

                {useAppointmentFilter &&
                !isLocationLoading &&
                !hasLocationActor ? (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{ py: 0.5, alignItems: "center" }}
                  >
                    Sem unidades (Location) para buscar consultas. Desative o
                    filtro de consultas ou verifique seu acesso à clínica.
                  </Alert>
                ) : null}

                {searchError != null ? (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{ py: 0.5, alignItems: "center" }}
                  >
                    {searchError}
                  </Alert>
                ) : null}
              </Stack>
            ) : null}

            <CommunicationsPatientFinderFilterCard
              title="Sexo"
              showOptionalLabel
              helperText='Deixe em "Todos" para não filtrar por sexo.'
            >
              <CommunicationsSegmentedFilterControl
                value={gender}
                onChange={(_, value: PatientFinderGender | null) => {
                  if (value != null) setGender(value);
                }}
                aria-label="Filtrar por sexo"
              >
                <ToggleButton value="all">Todos</ToggleButton>
                <ToggleButton value="female">Feminino</ToggleButton>
                <ToggleButton value="male">Masculino</ToggleButton>
                <ToggleButton value="other">Outro</ToggleButton>
              </CommunicationsSegmentedFilterControl>
            </CommunicationsPatientFinderFilterCard>

            <CommunicationsPatientFinderFilterCard
              title="Comunicação FluxZap"
              showOptionalLabel
              helperText='"Todos" ignora o histórico de envios nesta organização.'
            >
              <CommunicationsSegmentedFilterControl
                value={communicationFilter}
                onChange={(_, value: string | null) => {
                  if (
                    value != null &&
                    isPatientFinderCommunicationFilter(value)
                  ) {
                    setCommunicationFilter(value);
                  }
                }}
                aria-label="Filtrar por comunicação FluxZap"
              >
                <ToggleButton value="all">Todos</ToggleButton>
                <ToggleButton value="never">Ainda não comuniquei</ToggleButton>
                <ToggleButton value="already">Já comuniquei</ToggleButton>
              </CommunicationsSegmentedFilterControl>
            </CommunicationsPatientFinderFilterCard>

            <CommunicationsPatientFinderFilterCard
              title="Idade"
              showOptionalLabel
              helperText="Deixe os campos vazios para não filtrar por idade."
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                useFlexGap
                flexWrap="wrap"
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                sx={{ width: "100%" }}
              >
                <TextField
                  label="Idade mínima"
                  type="number"
                  size="medium"
                  value={minAge}
                  onChange={(e) => setMinAge(e.target.value)}
                  inputProps={{ min: 0, max: 130 }}
                  sx={patientFinderAgeFieldSx}
                />
                <TextField
                  label="Idade máxima"
                  type="number"
                  size="medium"
                  value={maxAge}
                  onChange={(e) => setMaxAge(e.target.value)}
                  inputProps={{ min: 0, max: 130 }}
                  sx={patientFinderAgeFieldSx}
                />
              </Stack>
            </CommunicationsPatientFinderFilterCard>

            {resultPatients != null ? (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack spacing={0.25}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {hasActiveListSearch &&
                        resultFilteredCount !== resultTotalCount
                          ? `Resultado (${resultFilteredCount} de ${resultTotalCount} ${
                              resultTotalCount === 1 ? "paciente" : "pacientes"
                            })`
                          : `Resultado (${resultTotalCount} ${
                              resultTotalCount === 1 ? "paciente" : "pacientes"
                            })`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {resultSummary}
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        ...communicationsRankTableToolbarSx,
                        mx: -1.5,
                        mt: -0.5,
                        borderRadius: "8px 8px 0 0",
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        variant="filled"
                        label="Buscar paciente"
                        placeholder="Nome, e-mail, telefone ou CPF…"
                        value={listSearchQuery}
                        onChange={(event) =>
                          setListSearchQuery(event.target.value)
                        }
                        sx={communicationsRankFilledSearchFieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" color="disabled" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {hasActiveListSearch && resultFilteredCount === 0 ? (
                      <Box sx={{ px: 0.5, py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum paciente corresponde à busca.
                        </Typography>
                      </Box>
                    ) : (
                      <CommunicationsShortcutPatientListBlock
                        shortcutPatients={filteredResultPatients ?? []}
                        selectionResetKey={resultListGeneration}
                        showLastAppointmentColumn={resultShowLastAppointment}
                        communications={communications}
                        onOpenSendMessage={onOpenSendMessage}
                      />
                    )}
                  </Stack>
                </Paper>
              </>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <Button
            variant="outlined"
            size="medium"
            startIcon={<RestartAltIcon />}
            onClick={applyDefaultFormState}
            disabled={isSearching}
            sx={{
              ...actionButtonSx,
              bgcolor: "background.paper",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Limpar filtros
          </Button>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              variant="contained"
              size="medium"
              onClick={() => void handleList()}
              disabled={
                isSearching || needsLocationForSearch || waitingAudienceData
              }
              startIcon={
                isSearching ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
              sx={{
                ...actionButtonSx,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {isSearching ? "Listando…" : "Listar pacientes"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  },
);

CommunicationsFindPatientsDialog.displayName =
  "CommunicationsFindPatientsDialog";

export default CommunicationsFindPatientsDialog;
